import { isArray } from '@ember/array';
import { isBlank } from '@ember/utils';
import type { ProductRow } from '../services/state';

interface JsonProductParameterValue {
  basevalue: string;
  baseuom: string | null;
  displayvalue: string;
  displayuom: string | null;
}
interface JsonProductParameter {
  id: string;
  datatype: string;
  valuetype: string;
  value: JsonProductParameterValue[];
}
export interface JsonProduct {
  id: string;
  display: string;
  mfr: string;
  rank1: string;
  rank2: string;
  parameters: JsonProductParameter[];
}

const TOOL_NAME_USED = 'DDSWebTool';

// The result will be undefined if the specified id doesn't exist in this part
const findParameter = function (product: JsonProduct, id: string): JsonProductParameter | undefined {
  return product.parameters.find((item) => {
    return item.id === id;
  });
};

// The result will be undefined if the input is undefined or the value array is empty
const getBaseValue = function (parameter?: JsonProductParameter | undefined): string | undefined {
  return parameter ? parameter.value[0]?.basevalue : undefined;
};

// The result will be undefined if the input is undefined or can't be parsed as a float
const parseOptionalFloat = function (text: string | undefined): number | undefined {
  if (typeof text === 'undefined' || isBlank(text)) {
    return undefined;
  }
  const result = parseFloat(text);
  return isNaN(result) ? undefined : result;
};

// The result will be undefined if the input is undefined or can't be parsed as an integer
const parseOptionalInt = function (text: string | undefined): number | undefined {
  if (typeof text === 'undefined' || isBlank(text)) {
    return undefined;
  }
  const result = parseInt(text);
  return isNaN(result) ? undefined : result;
};

const numericSort = function (a: number, b: number) {
  if (Number(a) < Number(b)) {
    return -1;
  }
  if (Number(a) > Number(b)) {
    return 1;
  }
  return 0;
};

const hasMandatoryFields = (product: Product): boolean => {
  return (
    typeof product.resolution !== 'undefined' &&
    typeof product.masterFclk !== 'undefined' &&
    typeof product.tuningWordWidth !== 'undefined' &&
    typeof product.discardedBits !== 'undefined'
  );
};
export const BuildProductRows = (products: JsonProduct[]): ProductRow[] => {
  const jsonProducts = products.filter((item) => {
    return item.parameters.some((itemParam) => {
      return itemParam.value.some((val) => val.basevalue === TOOL_NAME_USED) && itemParam.id === '2663';
    });
  });

  const rows = [];
  for (const jsonProduct of jsonProducts) {
    const product = new Product(jsonProduct);
    if (hasMandatoryFields(product)) {
      rows.push({
        name: product.name,
        resolution: product.resolution as number,
        masterFclk: product.masterFclk as number,
        tuningWordWidth: product.tuningWordWidth as number,
        discardedBits: product.discardedBits as number,
        multipliers: product.multipliers ?? null,
        refClkMinFreq: product.refClkMinFreq ?? null, //TODO: Deal with undefined value here
        refClkMinFreqWMult: product.refClkMinFreqWMult ?? null,
        spurCoeff1: product.spurCoeff1 ?? null,
        spurCoeff2: product.spurCoeff2 ?? null,
        spurCoeff3: product.spurCoeff3 ?? null,
        spurCoeff4: product.spurCoeff4 ?? null,
        spurCoeff5: product.spurCoeff5 ?? null,
        spurCoeff6: product.spurCoeff6 ?? null,
      });
    } else {
      console.log('Product with missing mandatory fields: ', product);
    }
  }
  return rows;
};
export default class Product {
  name: string;
  resolution?: number;
  masterFclk?: number;
  tuningWordWidth?: number;
  refClkMinFreq?: number;
  discardedBits?: number;
  multipliers?: number[];
  refClkMinFreqWMult?: number;
  spurCoeff1?: number;
  spurCoeff2?: number;
  spurCoeff3?: number;
  spurCoeff4?: number;
  spurCoeff5?: number;
  spurCoeff6?: number;
  constructor(product: JsonProduct) {
    this.name = product.id;
    this.resolution = parseOptionalInt(getBaseValue(findParameter(product, '7')));
    this.masterFclk = parseOptionalFloat(getBaseValue(findParameter(product, '379')));
    this.tuningWordWidth = parseOptionalInt(getBaseValue(findParameter(product, '1225')));
    this.discardedBits = parseOptionalFloat(getBaseValue(findParameter(product, '2981')));
    this.refClkMinFreq = parseOptionalFloat(getBaseValue(findParameter(product, '3013')));
    this.refClkMinFreqWMult = parseOptionalFloat(getBaseValue(findParameter(product, '3014')));
    this.spurCoeff1 = parseOptionalFloat(getBaseValue(findParameter(product, '2975')));
    this.spurCoeff2 = parseOptionalFloat(getBaseValue(findParameter(product, '2976')));
    this.spurCoeff3 = parseOptionalFloat(getBaseValue(findParameter(product, '2977')));
    this.spurCoeff4 = parseOptionalFloat(getBaseValue(findParameter(product, '2978')));
    this.spurCoeff5 = parseOptionalFloat(getBaseValue(findParameter(product, '2979')));
    this.spurCoeff6 = parseOptionalFloat(getBaseValue(findParameter(product, '2980')));
    const productMultipliers = findParameter(product, '2982');
    if (productMultipliers && !isBlank(productMultipliers) && isArray(productMultipliers.value)) {
      this.multipliers = productMultipliers.value
        .map(function (item) {
          return parseInt(item.basevalue);
        })
        .sort(numericSort);
    } else {
      this.multipliers = [];
    }
  }
}
